import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from "@mui/material";
import { FiEdit, FiPlus } from "react-icons/fi";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import ConfirmModal from "../../components/ui/ConfirmModal";
import { useAllUsers } from "./hooks/useAllUsers";
import { useCreateUser } from "./hooks/useCreateUser";
import { useUpdateUser } from "./hooks/useUpdateUser";
import { useFeedback } from "./hooks/FeedbackContext";
import { useTableState } from "./hooks/useTableState";
import TableFilters from "./components/TableFilter";
import MultiSelectFilter from "./components/MultiSelectFilter";
import { STATUS_OPTIONS } from "./constants/enums";


// ─── Mapeo de roles ───────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { rolId: 1, label: "Admin", nombre: "ADMIN" },
  { rolId: 2, label: "Editor Blog", nombre: "EDITOR_BLOG" },
  { rolId: 3, label: "Recruiter", nombre: "RECRUITER" },
];

const ROLE_CHIP_COLOR: Record<string, "secondary" | "primary" | "warning" | "default"> = {
  ADMIN: "secondary",
  EDITOR_BLOG: "primary",
  RECRUITER: "warning",
};

const ROLE_FILTER_OPTIONS = ROLE_OPTIONS.map((r) => ({ value: r.nombre, label: r.label }));

// ─── Componente ───────────────────────────────────────────────────────────────

const UsuariosBackoffice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { showFeedback } = useFeedback();

  const isNewRoute = location.pathname === "/admin/usuarios/new";
  const isEditRoute = location.pathname.startsWith("/admin/usuarios/edit");
  const isFormOpen = isNewRoute || isEditRoute;

  // ─── Hooks de API ──────────────────────────────────────────────────────
  const { users, loading, error, refetch } = useAllUsers();
  const { createUser, loading: createLoading } = useCreateUser();
  const { updateUser, loading: updateLoading } = useUpdateUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ─── Estados locales ───────────────────────────────────────────────────
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({ nombre: "", email: "", rolId: 2, activo: true });

  const isSaving = createLoading || updateLoading;

  // ─── Enriquecemos los datos para que el sort funcione sobre campos planos ──
  const enrichedUsers = useMemo(() => {
    if (!users) return [];
    return users.map((u) => ({
      ...u,
      rolNombre: u.rol?.nombre || "",
    }));
  }, [users]);

  // ─── Filtros y ordenamiento ────────────────────────────────────────────
  const {
    search,
    setSearch,
    filters,
    setSpecificFilter,
    resetFilters,
    hasActiveFilters,
    order,
    orderBy,
    handleRequestSort,
    processedData: filteredUsers,
  } = useTableState({
    data: enrichedUsers,
    initialSortKey: "nombre",
    searchExtractor: (item) => item.nombre,
  });

  // ─── Cargar datos al editar ────────────────────────────────────────────
  useEffect(() => {
    if (isEditRoute && id) {
      const user = users.find((u) => u.usuarioId === Number(id));
      if (user) {
        setFormData({
          nombre: user.nombre,
          email: user.email,
          rolId: user.rol?.rolId ?? 2,
          activo: user.activo,
        });
      }
    } else {
      setFormData({ nombre: "", email: "", rolId: 2, activo: true });
    }
  }, [location.pathname, id, users]);

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePreSave = (e: React.FormEvent) => {
    e.preventDefault();
    setIsConfirmOpen(true);
  };

  const handleExecuteSave = async () => {
    setIsConfirmOpen(false);

    try {
      if (isEditRoute && id) {
        await updateUser(Number(id), {
          nombre: formData.nombre,
          email: formData.email,
          rolId: formData.rolId,
          activo: formData.activo,
        });
        showFeedback("Usuario actualizado correctamente", "success");
      } else {
        await createUser({
          nombre: formData.nombre,
          email: formData.email,
          rolId: formData.rolId,
        });
        showFeedback("Usuario creado correctamente", "success");
      }
      refetch();
      navigate("/admin/usuarios");
    } catch (err: any) {
      showFeedback(
        err?.message && err.message !== "Network Error"
          ? err.message
          : "Ocurrió un error al guardar el usuario",
        "error"
      );
    }
  };

  return (
    <Box sx={{ width: "100%" }}>

      {/* TÍTULO Y BOTÓN SUPERIOR */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          Listado de Usuarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<FiPlus />}
          onClick={() => navigate("/admin/usuarios/new")}
          sx={{
            bgcolor: "primary.main",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            padding: "8px 16px",
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          Nuevo Usuario
        </Button>
      </Box>

      {/* CONTENIDO PRINCIPAL */}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "40vh" }}>
          <CircularProgress size={52} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "40vh",
            gap: 2,
            px: 3,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "error.light",
            borderRadius: "12px",
            bgcolor: "background.paper",
            py: 4,
          }}
        >
          <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
            Error al cargar los usuarios
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
            No pudimos obtener la lista de usuarios. Por favor, verifica tu conexión e intenta nuevamente.
          </Typography>
          <Button
            variant="contained"
            onClick={() => refetch()}
            sx={{ mt: 1, borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
          >
            Reintentar
          </Button>
        </Box>
      ) : !users || users.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "30vh",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: "12px",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            No se encontraron usuarios.
          </Typography>
        </Box>
      ) : (
        <>
          {/* FILTROS */}
          <TableFilters
            search={search}
            onSearchChange={setSearch}
            onClear={resetFilters}
            hasActiveFilters={hasActiveFilters}
            searchPlaceholder="Buscar por nombre..."
          >
            <MultiSelectFilter
              label="Rol"
              value={filters["rolNombre"] || []}
              options={ROLE_FILTER_OPTIONS}
              onChange={(vals) => setSpecificFilter("rolNombre", vals)}
            />
            <MultiSelectFilter
              label="Estado"
              value={filters["activo"] || []}
              options={STATUS_OPTIONS}
              onChange={(vals) => setSpecificFilter("activo", vals)}
            />
          </TableFilters>

          {filteredUsers.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", height: "20vh", alignItems: "center" }}>
              <Typography color="text.secondary">No se encontraron registros coincidentes.</Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {filteredUsers.map((row) => (
                <Card key={row.usuarioId} variant="outlined" sx={{ borderRadius: "12px", borderColor: "divider" }}>
                  <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 2, "&:last-child": { pb: 2 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
                        <Avatar sx={{ flexShrink: 0, bgcolor: "#e2e8f0", color: "#64748b", fontWeight: 600, fontSize: "0.875rem" }}>
                          {row.nombre.split(" ").map((n) => n[0]).join("")}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography noWrap sx={{ fontWeight: 600, color: "text.primary", fontSize: "0.95rem" }}>
                            {row.nombre}
                          </Typography>
                          <Typography noWrap sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                            {row.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/usuarios/edit/${row.usuarioId}`)}
                          sx={{ color: "text.secondary" }}
                        >
                          <FiEdit />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                      <Chip
                        label={row.rol?.nombre || "—"}
                        size="small"
                        color={ROLE_CHIP_COLOR[row.rol?.nombre] || "default"}
                        sx={{ fontWeight: 700, fontSize: "0.7rem", borderRadius: 1, color: "#ffffff" }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            bgcolor: row.activo ? "#22c55e" : "#ef4444",
                            borderRadius: "50%",
                          }}
                        />
                        <Typography
                          sx={{
                            color: row.activo ? "#22c55e" : "#ef4444",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                          }}
                        >
                          {row.activo ? "Activo" : "Inactivo"}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "divider",
                "& .MuiTableCell-head": {
                  bgcolor: "background.default",
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                },
              }}
            >
              <Table sx={{ minWidth: 650, tableLayout:'fixed' }} aria-label="tabla usuarios backoffice">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "45%" }}>
                      <TableSortLabel
                        active={orderBy === "nombre" && order !== null}
                        direction={orderBy === "nombre" && order ? order : "asc"}
                        onClick={() => handleRequestSort("nombre")}
                      >
                        USUARIO
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: "15%" }}>
                      <TableSortLabel
                        active={orderBy === "rolNombre" && order !== null}
                        direction={orderBy === "rolNombre" && order ? order : "asc"}
                        onClick={() => handleRequestSort("rolNombre")}
                      >
                        PERFIL / ROL
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: "15%" }}>
                      <TableSortLabel
                        active={orderBy === "activo" && order !== null}
                        direction={orderBy === "activo" && order ? order : "asc"}
                        onClick={() => handleRequestSort("activo")}
                      >
                        ESTADO
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: "15%" }} align="right">ACCIONES</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((row) => (
                    <TableRow
                      key={row.usuarioId}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:hover": { bgcolor: "background.default" },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar sx={{ bgcolor: "#e2e8f0", color: "#64748b", fontWeight: 600, fontSize: "0.875rem" }}>
                            {row.nombre.split(" ").map((n) => n[0]).join("")}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 600, color: "text.primary", fontSize: "0.9rem" }}>
                              {row.nombre}
                            </Typography>
                            <Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                              {row.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.rol?.nombre || "—"}
                          size="small"
                          color={ROLE_CHIP_COLOR[row.rol?.nombre] || "default"}
                          sx={{ fontWeight: 700, fontSize: "0.7rem", borderRadius: 1, color: "#ffffff" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              bgcolor: row.activo ? "#22c55e" : "#ef4444",
                              borderRadius: "50%",
                            }}
                          />
                          <Typography
                            sx={{
                              color: row.activo ? "#22c55e" : "#ef4444",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                            }}
                          >
                            {row.activo ? "Activo" : "Inactivo"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/usuarios/edit/${row.usuarioId}`)}
                          sx={{ color: "text.secondary", mr: 1 }}
                        >
                          <FiEdit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* DIALOG DE FORMULARIO (CREAR / EDITAR) */}
      <Dialog
        open={isFormOpen}
        onClose={!isSaving ? () => navigate("/admin/usuarios") : undefined}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            padding: 2,
            width: "100%",
            maxWidth: "450px",
          },
        }}
      >
        <form onSubmit={handlePreSave}>
          <DialogTitle sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#0f172a", pb: 1 }}>
            {isEditRoute ? "Editar Usuario" : "Nuevo Usuario"}
          </DialogTitle>

          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            <Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.5, color: "#334155" }}>
                Nombre Completo
              </Typography>
              <TextField
                fullWidth
                placeholder="Ej. Juan Pérez"
                value={formData.nombre}
                onChange={(e) => handleInputChange("nombre", e.target.value)}
                required
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.5, color: "#334155" }}>
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder="Ej. juan@empresa.com"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                required
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.5, color: "#334155" }}>
                  Rol
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.rolId}
                    onChange={(e) => handleInputChange("rolId", e.target.value as number)}
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <MenuItem key={role.rolId} value={role.rolId}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {isEditRoute && (
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.5, color: "#334155" }}>
                    Estado
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.activo ? "true" : "false"}
                      onChange={(e) => handleInputChange("activo", e.target.value === "true")}
                    >
                      <MenuItem value="true">Activo</MenuItem>
                      <MenuItem value="false">Inactivo</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 1, pt: 2, gap: 1 }}>
            <Button
              onClick={() => navigate("/admin/usuarios")}
              disabled={isSaving}
              sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              sx={{
                textTransform: "none",
                bgcolor: "#1d4ed8",
                px: 3,
                borderRadius: 2,
                fontWeight: 600,
                minWidth: 110,
              }}
            >
              {isSaving ? <CircularProgress size={24} color="inherit" /> : "Guardar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* CONFIRMACIÓN DE GUARDAR */}
      <ConfirmModal
        open={isConfirmOpen}
        title={isEditRoute ? "Confirmar modificación" : "Confirmar invitación"}
        content={
          isEditRoute
            ? `¿Estás seguro de que deseas aplicar los cambios sobre el usuario ${formData.nombre}?`
            : `Se enviará un correo de alta a ${formData.email} con el rol asignado. ¿Deseas continuar?`
        }
        onConfirm={handleExecuteSave}
        onCancel={() => setIsConfirmOpen(false)}
        loading={isSaving}
      />
    </Box>
  );
};

export default UsuariosBackoffice;
